import { lazy, Suspense } from "react";
import { Button } from "../../components/ui/Button";
import { EmptyState, Spinner } from "../../components/ui/misc";
import { Panel } from "../../components/ui/Panel";
import { can, useMe } from "../../lib/auth";
import { useSites } from "../../lib/queries";
import { useClient } from "../clients/client-ui";

// The view brings React Flow with it: it is fetched when the tab is opened, not with the app.
const DiagramView = lazy(() => import("./DiagramView"));

/** The tab's chunk could not be fetched (typically a deploy replaced it while the page was open). */
export function DiagramLoadError() {
  return (
    <Panel aria-label="Diagram" title="Diagram">
      <EmptyState
        title="The diagram could not be loaded"
        body="The page may be out of date after an update."
        action={<Button onClick={() => window.location.reload()}>Reload</Button>}
      />
    </Panel>
  );
}

/** The Diagram tab: one network diagram per site of the client (network diagram spec §7.1). */
export function ClientDiagram({ clientId, siteId }: { clientId: string; siteId?: string }) {
  const me = useMe();
  const { client } = useClient(clientId);
  const { data: sites, isLoading } = useSites(clientId);
  const site = siteId ? sites?.find((s) => s.id === siteId) : sites?.[0];

  let body = null;
  if (client && !client.synced)
    body = <EmptyState title="This client is not linked to Gorelo, so it has no diagram." />;
  else if (isLoading || !client) body = <Spinner label="Loading sites…" />;
  else if (!sites) body = <EmptyState title="The sites could not be loaded" />;
  else if (sites.length === 0)
    body = <EmptyState title="This client has no site in Gorelo. A diagram belongs to a site." />;
  else if (!site) body = <EmptyState title="This site does not exist for this client" />;
  else
    body = (
      <Suspense fallback={<Spinner label="Loading diagram…" />}>
        <DiagramView key={site.id} clientId={clientId} site={site} sites={sites} canEdit={can(me, "edit")} />
      </Suspense>
    );

  return (
    <Panel aria-label="Diagram" title="Diagram">
      {body}
    </Panel>
  );
}
